/**
 * direct-io.ts — main-thread setup for the SAB-AVIO plumbing (A-P1.0).
 *
 * Allocates the two SharedArrayBuffers of the sab-protocol, spawns the
 * dedicated fetch-worker, and hands it the resolved stream URL. Returns a
 * DirectIo handle whose `ctrlSab`/`dataSab` are structured-clone-passed to the
 * player-worker (where libav's read-callback consumes them via
 * `globalThis.__zapdSab` + `zapd_open_stream`).
 *
 * This is pure plumbing: no routing, no orchestrator wiring (that is A-P1.1).
 * The future DirectSource orchestrator calls `createDirectIo()`, forwards the
 * SABs to the player-worker, and drives `abort()`/`resetAbort()` around seeks.
 */

import FetchWorkerUrl from './sab-fetch-worker.ts?worker&url';
import {
  Ctrl, CTRL_SLOTS, DATA_WINDOW_BYTES,
  type FetchWorkerCmd,
} from './sab-protocol';

export interface CreateDirectIoOpts {
  /** Stream endpoint (e.g. `/api-zapd/stream/<sid>`), absolute or relative. */
  url:          string;
  /** Total file size (streamOpen Content-Length) — bounds readahead + AVSEEK_SIZE. */
  fileSize:     number;
  /** Access token appended as `?token=` when the endpoint isn't cookie-authed. */
  token?:       string | null;
  /** fetch credentials mode. Default 'include' (cookie auth on same-origin). */
  credentials?: RequestCredentials;
}

export interface DirectIo {
  /** Control-SAB — pass to the player-worker; the orchestrator also drives ABORT here. */
  readonly ctrlSab:  SharedArrayBuffer;
  /** Data-SAB (readahead window) — pass to the player-worker. */
  readonly dataSab:  SharedArrayBuffer;
  /** Total file size (for the demuxer's AVSEEK_SIZE). */
  readonly fileSize: number;
  /**
   * Cancel the in-flight read (seek-cancel): sets ABORT, wakes the demuxer
   * (which returns AVERROR), and aborts the fetch. Call `resetAbort()` before
   * the next read.
   */
  abort(): void;
  /** Clear ABORT so the fetcher serves the next request. */
  resetAbort(): void;
  /** Full teardown: abort + terminate the fetch-worker. Idempotent. */
  dispose(): void;
}

/** Append `?token=` (preserving any existing query) when a token is supplied. */
function withToken(url: string, token?: string | null): string {
  if (!token) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}token=${encodeURIComponent(token)}`;
}

export function createDirectIo(opts: CreateDirectIoOpts): DirectIo {
  if (typeof SharedArrayBuffer === 'undefined') {
    // COOP/COEP is live on the ZAP vhost, so this only trips in a mis-configured
    // context — fail loud rather than silently degrade.
    throw new Error('createDirectIo: SharedArrayBuffer unavailable (COOP/COEP not active?)');
  }

  const ctrlSab = new SharedArrayBuffer(CTRL_SLOTS * 4);
  const dataSab = new SharedArrayBuffer(DATA_WINDOW_BYTES);
  const ctrl    = new Int32Array(ctrlSab);

  const worker = new Worker(FetchWorkerUrl, { type: 'classic' });
  const post = (cmd: FetchWorkerCmd): void => {
    try { worker.postMessage(cmd); } catch { /* worker already gone */ }
  };

  post({
    type:        'init',
    ctrlSab,
    dataSab,
    url:         withToken(opts.url, opts.token),
    credentials: opts.credentials ?? 'include',
    fileSize:    opts.fileSize,
  });

  let disposed = false;

  return {
    ctrlSab,
    dataSab,
    fileSize: opts.fileSize,

    abort(): void {
      Atomics.store(ctrl, Ctrl.ABORT, 1);
      Atomics.notify(ctrl, Ctrl.STATE);
      post({ type: 'abort' });
    },

    resetAbort(): void {
      Atomics.store(ctrl, Ctrl.ABORT, 0);
    },

    dispose(): void {
      if (disposed) return;
      disposed = true;
      Atomics.store(ctrl, Ctrl.ABORT, 1);
      Atomics.notify(ctrl, Ctrl.STATE);
      post({ type: 'teardown' });
      // Give the worker a tick to abort its in-flight fetch before we cut it off.
      setTimeout(() => { try { worker.terminate(); } catch { /* noop */ } }, 0);
    },
  };
}
